import { LOCAL_STORAGE } from "@constants/index";
import { PermissionContext } from "@contexts/PermissionContext";
import { useContext, useMemo } from "react";

const isCurrentUser = (userId) => {
  const loggedInUserId = localStorage.getItem(LOCAL_STORAGE.USER_ID);
  if (userId == null || userId === "" || !loggedInUserId) return false;
  return String(loggedInUserId) === String(userId);
};

/**
 * Project-detail permissions: global role (PermissionContext) + scope for the open project.
 * - Product Manager: product.productManager must match the logged-in user.
 * - Project Manager: projectManagerId must match the logged-in user.
 * - SuperUser: full access (hasManagePermission).
 */
export const useProjectDetailPermissions = (project) => {
  const {
    hasManagePermission,
    hasSuperUserPermission,
    hasProductManagerPermission,
    hasProjectManagerPermission,
  } = useContext(PermissionContext);

  return useMemo(() => {
    const isProductManagerOfProjectProduct = isCurrentUser(project?.product?.productManager);
    const isProjectManagerOfProject = isCurrentUser(project?.projectManagerId);

    const canEditProjectManagerField =
      hasManagePermission || (hasProductManagerPermission && isProductManagerOfProjectProduct);

    const canEditProjectProductNameField =
      hasManagePermission || (hasProductManagerPermission && isProductManagerOfProjectProduct);

    const canAddProjectMemberPermission =
      hasManagePermission || (hasProjectManagerPermission && isProjectManagerOfProject);

    const canDeleteProjectMemberPermission =
      hasManagePermission || (hasProjectManagerPermission && isProjectManagerOfProject);

    const canEditProjectInformationPermission =
      hasManagePermission
      || (hasProductManagerPermission && isProductManagerOfProjectProduct)
      || (hasProjectManagerPermission && isProjectManagerOfProject);

    return {
      canEditProjectManagerField,
      canEditProjectProductNameField,
      canAddProjectMemberPermission,
      canDeleteProjectMemberPermission,
      canEditProjectInformationPermission,
      isProductManagerOfProjectProduct,
      isProjectManagerOfProject,
    };
  }, [
    project?.product?.productManager,
    project?.projectManagerId,
    hasManagePermission,
    hasSuperUserPermission,
    hasProductManagerPermission,
    hasProjectManagerPermission,
  ]);
};
